// src/components/DashCalculator.jsx
import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './dash.css';
import './dash-pdf.css';

const MEALS = [
  'BREAKFAST',
  'MORNING SNACK',
  'LUNCH',
  'AFTERNOON SNACK',
  'DINNER',
];

const LOGO_URL =
  'https://onemed.clinic/wp-content/uploads/2023/11/17009d89d69bdae16833d13d2c812ea68dda52ad.png';

// Excel path from HTML parameter
const EXCEL_PATH =
  typeof document !== 'undefined'
    ? document.getElementById('dash-root')?.dataset?.excel || '/calculator.xlsx'
    : '/calculator.xlsx';

// -----------------------
// MEAL SECTION COMPONENT
// -----------------------
const MealSection = ({
  meal,
  groupedData,
  items,
  onTryAdd,
  onRemoveItem,
  error,
  exporting,
}) => {
  const [group, setGroup] = useState('');
  const [food, setFood] = useState('');
  const [servingSize, setServingSize] = useState('');

  const groups = Object.keys(groupedData);

  const foodsForGroup = useMemo(() => {
    if (!group) return [];
    return Object.keys(groupedData[group] || {});
  }, [group, groupedData]);

  const servingSizeOptions = useMemo(() => {
    if (!group || !food) return [];
    return groupedData[group][food]?.sizes || [];
  }, [group, food, groupedData]);

  const resetSelectors = () => {
    setGroup('');
    setFood('');
    setServingSize('');
  };

  const mealItems = items.filter((i) => i.meal === meal);
  console.log('I was triggered during render');
  // const sort = foodsForGroup.sort((a, b) => (a > b ? -1 : 1));
  // console.log(sort);
  return (
    <div class='dash-card dash-meal-card'>
      <h3 class='dash-meal-title'>{meal}</h3>

      <div class={`dash-row ${exporting ? 'hidden-export' : ''}`}>
        {/* GROUP */}
        <label>
          Food Group
          <select
            value={group}
            onChange={(e) => {
              setGroup(e.target.value);
              setFood('');
              setServingSize('');
            }}
          >
            <option value=''>Select group...</option>
            {groups.map((g) => (
              <option key={g}>{g}</option>
            ))}
          </select>
        </label>

        {/* FOOD */}
        <label>
          Food
          <select
            value={food}
            onChange={(e) => {
              setFood(e.target.value);
              setServingSize('');
            }}
            disabled={!group}
          >
            <option value=''>
              {group ? 'Select food...' : 'Select a group first...'}
            </option>
            {foodsForGroup
              .sort((a, b) => a.localeCompare(b))
              .map((f) => (
                <option key={f}>{f}</option>
              ))}
          </select>
        </label>

        {/* SERVINGS SIZE */}
        <label>
          Servings size
          <select
            value={servingSize}
            onChange={(e) => setServingSize(e.target.value)}
            disabled={!food}
          >
            <option value=''>
              {food ? 'Select serving size...' : 'Select a food first...'}
            </option>
            {servingSizeOptions.map((s, i) => (
              <option key={i}>{s}</option>
            ))}
          </select>
        </label>

        {!exporting && (
          <button
            class='dash-add-btn'
            onClick={() =>
              onTryAdd({
                meal,
                group,
                food,
                servingSize,
                reset: resetSelectors,
              })
            }
          >
            Add
          </button>
        )}
      </div>

      {error && <div class='dash-error'>{error}</div>}

      {/* ADDED ITEMS */}
      {mealItems.length > 0 && (
        <div class='dash-meal-list'>
          {mealItems.map((i) => (
            <div key={i.id} class='dash-list-item'>
              <div>
                <strong>{i.food}</strong>{' '}
                <span class='dash-pill'>{i.group}</span>
                <span class='dash-size'>{i.servingSize}</span>
              </div>

              {!exporting && (
                <button
                  class='dash-remove-btn'
                  onClick={() => onRemoveItem(i.id)}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -----------------------
// MAIN COMPONENT
// -----------------------
const DashCalculator = () => {
  const [rawData, setRawData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [items, setItems] = useState([]);
  const [errorByMeal, setErrorByMeal] = useState({});
  const [exporting, setExporting] = useState(false);

  // -----------------------
  // LOAD EXCEL
  // -----------------------
  useEffect(() => {
    const loadExcel = async () => {
      try {
        const res = await fetch(EXCEL_PATH);
        const buf = await res.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets['DASH'] || wb.Sheets[wb.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

        const cleaned = rows
          .map((r) => ({
            group: r['Froud Group'],
            food: r['Food'],
            servingSize: r['Servings size'],
            servingValue: r['SERVINGS per day Food'],
            limit: Number(r['SERVINGS PER DAY']) || 0,
          }))
          .filter((r) => r.group && r.food);

        setRawData(cleaned);
      } catch (e) {
        setLoadError('Error loading Excel file');
      } finally {
        setLoading(false);
      }
    };

    loadExcel();
  }, []);

  // -----------------------
  // GROUP BY Food
  // -----------------------
  useEffect(() => {
    const map = {};

    rawData.forEach((row) => {
      if (!map[row.group]) map[row.group] = {};
      if (!map[row.group][row.food]) {
        map[row.group][row.food] = {
          sizes: [],
          servingValuesBySize: {},
          limit: row.limit,
        };
      }

      // Add Servings size
      if (!map[row.group][row.food].sizes.includes(row.servingSize)) {
        map[row.group][row.food].sizes.push(row.servingSize);
      }

      // FIRST SERVINGS per day Food for each size
      if (!map[row.group][row.food].servingValuesBySize[row.servingSize]) {
        map[row.group][row.food].servingValuesBySize[row.servingSize] =
          row.servingValue;
      }
    });

    // Keep original order from Excel
    setGroupedData(map);
  }, [rawData]);

  const limits = useMemo(() => {
    const out = {};
    rawData.forEach((r) => {
      if (!out[r.group] || r.limit > out[r.group]) {
        out[r.group] = r.limit;
      }
    });
    return out;
  }, [rawData]);

  const totals = useMemo(() => {
    const t = {};
    items.forEach((i) => {
      t[i.group] = (t[i.group] || 0) + Number(i.value);
    });
    return t;
  }, [items]);

  const setMealError = (meal, msg) =>
    setErrorByMeal((prev) => ({ ...prev, [meal]: msg }));

  // -----------------------
  // ADD ITEM
  // -----------------------
  const handleTryAdd = ({ meal, group, food, servingSize, reset }) => {
    if (!group || !food || !servingSize) {
      setMealError(meal, 'Please select group, food and serving size.');
      return;
    }

    const value = groupedData[group][food].servingValuesBySize[servingSize];

    const used = totals[group] || 0;
    const limit = limits[group];

    if (used + Number(value) > limit) {
      setMealError(
        meal,
        `Daily limit exceeded for "${group}". Limit: ${limit}, used: ${used}.`
      );
      return;
    }

    setItems((prev) => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        meal,
        group,
        food,
        servingSize,
        value: Number(value),
      },
    ]);

    setMealError(meal, '');
    reset();
  };

  const handleRemove = (id) =>
    setItems((prev) => prev.filter((i) => i.id !== id));

  // -----------------------
  // EXPORT PDF
  // -----------------------
  const exportPDF = async () => {
    // setExporting(true);

    // let React render pdf layout
    await new Promise((r) => setTimeout(r, 300));

    const pdfElement = document.getElementById('pdf-layout');

    const canvas = await html2canvas(pdfElement, {
      scale: 2, // High clarity but fits in A4
      useCORS: true,
    });

    const img = canvas.toDataURL('image/png');

    const pdf = new jsPDF('p', 'mm', 'a4');
    const width = pdf.internal.pageSize.getWidth();
    const height = (canvas.height * width) / canvas.width;

    pdf.addImage(img, 'PNG', 0, 0, width, height);
    pdf.save('dash-calculator.pdf');

    // setExporting(false);
  };

  if (loading) return <p>Loading DASH data…</p>;
  if (loadError) return <p style={{ color: 'red' }}>{loadError}</p>;

  const groups = Object.keys(groupedData);

  return (
    <div
      id='dash-root'
      class={`dash-container ${exporting ? 'exporting' : ''}`}
    >
      <div class='dash-left'>
        {MEALS.map((meal) => (
          <MealSection
            key={meal}
            meal={meal}
            groupedData={groupedData}
            items={items}
            onTryAdd={handleTryAdd}
            onRemoveItem={handleRemove}
            error={errorByMeal[meal] || ''}
            exporting={exporting}
          />
        ))}
      </div>

      {/* SUMMARY */}
      <div class='dash-right'>
        <h3 class='dash-card-title'>SERVINGS PER DAY</h3>

        <div class='dash-summary-card'>
          <table class='dash-table'>
            <thead>
              <tr>
                <th>Food Group</th>
                <th>Allowed</th>
                <th>Used</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <tr
                  key={g}
                  class={
                    (totals[g] || 0) > limits[g]
                      ? 'dash-row-exceeded'
                      : (totals[g] || 0) === limits[g]
                      ? 'dash-row-full'
                      : ''
                  }
                >
                  <td>{g}</td>
                  <td>{limits[g]}</td>
                  <td>{totals[g] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p class='dash-hint'>
            If a row appears in red, the daily limit has been exceeded.
          </p>
          <p class='dash-hint'>
            Legal Disclaimer: The information provided on this website is for
            general informational purposes only and does not constitute medical
            advice. Do not act or refrain from acting based on any content on
            this site without first consulting a qualified professional in the
            relevant field.
          </p>

          {/* Export button BELOW the text */}
          {!exporting && (
            <button class='dash-export-btn' onClick={exportPDF}>
              Export to PDF
            </button>
          )}
        </div>
      </div>
      {/* ----------------------- PDF LAYOUT ----------------------- */}

      <div id='pdf-layout' className='pdf-render'>
        <div className='pdf-page'>
          {/* HEADER */}
          <div className='pdf-header'>
            <img src={LOGO_URL} className='pdf-logo' alt='Logo' />

            <div className='pdf-title-block'>
              <h1>DASH DIET DAILY CALCULATOR</h1>
              <h2>FOR 1,800 TO 2,000 CALORIES PER DAY</h2>
              <a
                className='pdf-link'
                href='https://onemed.clinic/dash-diet-meal-planner'
              >
                HTTPS://ONEMED.CLINIC/DASH-DIET-MEAL-PLANNER
              </a>
            </div>
          </div>

          {/* BODY */}
          <div className='pdf-body'>
            {/* LEFT COLUMN */}
            <div className='pdf-left'>
              {MEALS.map((meal) => (
                <div className='pdf-meal-card' key={meal}>
                  <h3>{meal}</h3>

                  {items
                    .filter((i) => i.meal === meal)
                    .map((i) => (
                      <div className='pdf-food-row' key={i.id}>
                        <strong>{i.food}</strong>
                        <span className='pdf-pill'>{i.group}</span>
                        <span className='pdf-size'>{i.servingSize}</span>
                      </div>
                    ))}
                </div>
              ))}
            </div>

            {/* RIGHT COLUMN */}
            <div className='pdf-right'>
              <h3 className='pdf-servings-title'>SERVINGS PER DAY</h3>

              <table className='pdf-table'>
                <thead>
                  <tr>
                    <th>Food Group</th>
                    <th>Allowed</th>
                    <th>Used</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr
                      key={g}
                      class={
                        (totals[g] || 0) > limits[g]
                          ? 'dash-row-exceeded'
                          : (totals[g] || 0) === limits[g]
                          ? 'dash-row-full'
                          : ''
                      }
                    >
                      <td>{g}</td>
                      <td>{limits[g]}</td>
                      <td>{totals[g] || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p className='pdf-note'>
                If a row appears in red, the daily limit has been exceeded.
              </p>

              <p className='pdf-legal'>
                Legal Disclaimer: The information provided on this website is
                for general informational purposes only and does not constitute
                medical advice…
              </p>
            </div>
          </div>

          {/* FOOTER */}
          <div className='pdf-footer'>
            LEARN MORE ABOUT THE DASH DIET :
            <a href='https://onemed.clinic/dash-diet'>
              HTTPS://ONEMED.CLINIC/DASH-DIET
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashCalculator;
